package top.wcpe.allincore.dsl.ir.model.overlay

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap
import kotlin.jvm.JvmStatic

/**
 * Overlay 描述符。
 */
class OverlayDescriptor(
    kind: OverlayDescriptor.OverlayKind?,
    val hostId: String?,
    anchor: AnchorMode?,
    contentRef: ContentRef?,
    val interactive: Boolean,
    animation: AnimationSpec?,
    dismissBehavior: DismissBehavior?,
    placement: PlacementSpec?,
    /** ADR-0036：tooltip 专用数据请求绑定（v30 起；旧字节流 / 非 tooltip overlay 为空 list）。 */
    dataBindings: List<TooltipDataBinding>?,
) {
    val kind: OverlayKind = kind ?: throw NullPointerException("kind")
    val anchor: AnchorMode = anchor ?: throw NullPointerException("anchor")
    val contentRef: ContentRef = contentRef ?: throw NullPointerException("contentRef")
    val animation: AnimationSpec = animation ?: throw NullPointerException("animation")
    val dismissBehavior: DismissBehavior = dismissBehavior ?: throw NullPointerException("dismissBehavior")
    val placement: PlacementSpec = placement ?: throw NullPointerException("placement")
    val dataBindings: List<TooltipDataBinding> =
        if (dataBindings == null || dataBindings.isEmpty()) emptyList() else ReadOnlyList(ArrayList(dataBindings))

    constructor(
        kind: OverlayKind?,
        hostId: String?,
        anchor: AnchorMode?,
        contentRef: ContentRef?,
        interactive: Boolean,
        animation: AnimationSpec?,
        dismissBehavior: DismissBehavior?,
        placement: PlacementSpec?,
    ) : this(kind, hostId, anchor, contentRef, interactive, animation, dismissBehavior, placement, emptyList())

    fun isInteractive(): Boolean = interactive

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is OverlayDescriptor) return false
        return interactive == other.interactive &&
            kind == other.kind &&
            hostId == other.hostId &&
            anchor == other.anchor &&
            contentRef == other.contentRef &&
            animation == other.animation &&
            dismissBehavior == other.dismissBehavior &&
            placement == other.placement &&
            dataBindings == other.dataBindings
    }

    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + kind.hashCode()
        result = 31 * result + (hostId?.hashCode() ?: 0)
        result = 31 * result + anchor.hashCode()
        result = 31 * result + contentRef.hashCode()
        result = 31 * result + interactive.hashCode()
        result = 31 * result + animation.hashCode()
        result = 31 * result + dismissBehavior.hashCode()
        result = 31 * result + placement.hashCode()
        result = 31 * result + dataBindings.hashCode()
        return result
    }

    override fun toString(): String =
        "OverlayDescriptor{" + kind +
            ",hostId=" + hostId +
            ",anchor=" + anchor +
            ",contentRef=" + contentRef +
            ",interactive=" + interactive +
            ",animation=" + animation +
            ",dismissBehavior=" + dismissBehavior +
            ",placement=" + placement +
            ",dataBindings=" + dataBindings + "}"

    /** Overlay 类型。 */
    enum class OverlayKind {
        TOOLTIP,
        TOOLTIP_INTERACTIVE,
        MODAL,
        RIGHT_CLICK_MENU,
        CONTEXT_MENU,
        CUSTOM;

        companion object {
            @JvmStatic
            fun fromOrdinal(ordinal: Int): OverlayKind {
                val values = values()
                if (ordinal < 0 || ordinal >= values.size) {
                    throw IllegalStateException("未知 OverlayKind ordinal: $ordinal")
                }
                return values[ordinal]
            }
        }
    }

    /** 锚点模式。 */
    enum class AnchorMode {
        BOX,
        MOUSE,
        SCREEN_CENTER,
        CUSTOM;

        companion object {
            @JvmStatic
            fun fromOrdinal(ordinal: Int): AnchorMode {
                val values = values()
                if (ordinal < 0 || ordinal >= values.size) {
                    throw IllegalStateException("未知 AnchorMode ordinal: $ordinal")
                }
                return values[ordinal]
            }
        }
    }

    /** 出场 / 进场动画规格。 */
    class AnimationSpec(
        val fade: Boolean,
        val scale: Boolean,
        val durationMs: Double,
        val fadeOutMs: Double,
        easing: String?,
    ) {
        val easing: String

        init {
            require(!(durationMs.isNaN() || durationMs < 0.0)) { "animation durationMs 必须为非负有限值: $durationMs" }
            require(!(fadeOutMs.isNaN() || fadeOutMs < 0.0)) { "animation fadeOutMs 必须为非负有限值: $fadeOutMs" }
            this.easing = easing ?: throw NullPointerException("easing")
        }

        constructor(fade: Boolean, scale: Boolean, durationMs: Double, easing: String?) :
            this(fade, scale, durationMs, durationMs, easing)

        // 复刻原 Java `isFade()` / `isScale()`：改为计算属性以保下游属性语法访问（读构造入参 fade/scale）。
        val isFade: Boolean
            get() = fade

        val isScale: Boolean
            get() = scale

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is AnimationSpec) return false
            return fade == other.fade &&
                scale == other.scale &&
                durationMs.compareTo(other.durationMs) == 0 &&
                fadeOutMs.compareTo(other.fadeOutMs) == 0 &&
                easing == other.easing
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + fade.hashCode()
            result = 31 * result + scale.hashCode()
            result = 31 * result + durationMs.hashCode()
            result = 31 * result + fadeOutMs.hashCode()
            result = 31 * result + easing.hashCode()
            return result
        }

        override fun toString(): String =
            "AnimationSpec{fade=" + fade +
                ",scale=" + scale +
                ",durationMs=" + durationMs +
                ",fadeOutMs=" + fadeOutMs +
                ",easing=" + easing + "}"
    }

    /** 关闭行为位掩码。 */
    class DismissBehavior private constructor(val mask: Int) {

        init {
            require(!(mask < 0 || mask > 0xFFFF)) { "dismissBehavior mask 超出 u16 范围: $mask" }
        }

        fun contains(flag: Flag?): Boolean =
            (mask and (flag ?: throw NullPointerException("flag")).mask()) != 0

        /** 返回当前掩码包含的所有标志，按枚举声明顺序排列、只读。 */
        fun getFlags(): Set<Flag> {
            val flags = LinkedHashSet<Flag>()
            for (flag in Flag.values()) {
                if (contains(flag)) {
                    flags.add(flag)
                }
            }
            return flags
        }

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is DismissBehavior) return false
            return mask == other.mask
        }

        override fun hashCode(): Int = mask

        override fun toString(): String = "DismissBehavior{" + getFlags() + "}"

        /** 关闭触发标志。 */
        enum class Flag(private val maskValue: Int) {
            HOVER_OUT(1 shl 0),
            ESC(1 shl 1),
            BACKDROP_CLICK(1 shl 2),
            OUTSIDE_CLICK(1 shl 3),
            ITEM_CLICK(1 shl 4),
            EXPLICIT(1 shl 5);

            fun mask(): Int = maskValue
        }

        companion object {
            @JvmStatic
            fun of(vararg flags: Flag): DismissBehavior {
                var mask = 0
                for (flag in flags) {
                    mask = mask or flag.mask()
                }
                return DismissBehavior(mask)
            }

            @JvmStatic
            fun fromMask(mask: Int): DismissBehavior = DismissBehavior(mask)
        }
    }

    /** 位置规格。 */
    class PlacementSpec(
        preferredDirection: Direction?,
        val padding: Double,
        val offsetX: Double,
        val offsetY: Double,
    ) {
        val preferredDirection: Direction

        init {
            this.preferredDirection = preferredDirection ?: throw NullPointerException("preferredDirection")
            require(!(padding.isNaN() || padding < 0.0)) { "padding 必须为非负有限值: $padding" }
            require(!(offsetX.isNaN() || offsetY.isNaN())) { "offset 必须为有限值" }
        }

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is PlacementSpec) return false
            return padding.compareTo(other.padding) == 0 &&
                offsetX.compareTo(other.offsetX) == 0 &&
                offsetY.compareTo(other.offsetY) == 0 &&
                preferredDirection == other.preferredDirection
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + preferredDirection.hashCode()
            result = 31 * result + padding.hashCode()
            result = 31 * result + offsetX.hashCode()
            result = 31 * result + offsetY.hashCode()
            return result
        }

        override fun toString(): String =
            "PlacementSpec{" + preferredDirection +
                ",padding=" + padding +
                ",offsetX=" + offsetX +
                ",offsetY=" + offsetY + "}"

        /** 首选方向。 */
        enum class Direction {
            AUTO,
            TOP,
            BOTTOM,
            LEFT,
            RIGHT;

            companion object {
                @JvmStatic
                fun fromOrdinal(ordinal: Int): Direction {
                    val values = values()
                    if (ordinal < 0 || ordinal >= values.size) {
                        throw IllegalStateException("未知 Direction ordinal: $ordinal")
                    }
                    return values[ordinal]
                }
            }
        }
    }

    /**
     * ADR-0036：tooltip 块内 `data NAME = net.tooltip("ns.handler", #{...})` 一条数据请求绑定的 IR 形态。
     *
     * [name] 为注入的 local 句柄名；[namespace]/[handler] 为请求通道；[payload] 为有序的
     * `key → 值表达式 blob`（ExprBlob 字节，运行时反序列化为 ExprNode 后按 hover 上下文求值）。
     */
    class TooltipDataBinding(
        name: String?,
        namespace: String?,
        handler: String?,
        payload: Map<String, ByteArray>?,
        condition: ByteArray?,
    ) {
        val name: String = name ?: throw NullPointerException("name")
        val namespace: String = namespace ?: throw NullPointerException("namespace")
        val handler: String = handler ?: throw NullPointerException("handler")

        /** 有序 payload：key → 值表达式 blob（ExprBlob 字节）。 */
        val payload: Map<String, ByteArray> =
            if (payload == null || payload.isEmpty()) emptyMap() else ReadOnlyOrderedMap(LinkedHashMap(payload))

        // ADR-0036（v31）：可选 `when` 条件门控表达式 blob；null 表示无条件。
        private val conditionInternal: ByteArray? = condition

        constructor(name: String?, namespace: String?, handler: String?, payload: Map<String, ByteArray>?) :
            this(name, namespace, handler, payload, null)

        /**
         * ADR-0036（v31）：可选 `when` 条件门控表达式 blob（ExprBlob 字节）；null 表示无条件。
         * 复刻原 Java `getCondition()`：暴露为属性 `condition`，保留 private backing。
         */
        val condition: ByteArray?
            get() = conditionInternal

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is TooltipDataBinding) return false
            if (name != other.name || namespace != other.namespace || handler != other.handler) {
                return false
            }
            if (!conditionInternal.contentEquals(other.conditionInternal)) return false
            if (payload.keys != other.payload.keys) return false
            for ((key, value) in payload) {
                if (!value.contentEquals(other.payload[key])) return false
            }
            return true
        }

        override fun hashCode(): Int {
            // 复刻 Objects.hash(name, namespace, handler) 后逐项 31 倍累乘。
            var h = 1
            h = 31 * h + name.hashCode()
            h = 31 * h + namespace.hashCode()
            h = 31 * h + handler.hashCode()
            h = 31 * h + conditionInternal.contentHashCode()
            for ((key, value) in payload) {
                h = 31 * h + key.hashCode() + value.contentHashCode()
            }
            return h
        }

        override fun toString(): String =
            "TooltipDataBinding{name=" + name + ",ns=" + namespace + ",handler=" + handler +
                ",payloadKeys=" + payload.keys + ",hasCondition=" + (conditionInternal != null) + "}"
    }
}
